/**
 * One SIM (tasks B6, B7).
 *
 * **Identity is rendered in full and copyable.** MSISDN, ICCID, IMSI and EID
 * are the customer's own identifiers (§7) and the APN is what somebody types
 * into a device — truncating either turns the page into something you cannot
 * act from, which is the only reason it exists.
 */

import type { PlanSummary, SimSummary } from '@rd/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider.js';
import { FEATURE, PERMISSION } from '../../auth/keys.js';
import { BackLink } from '../../components/BackLink.js';
import { Badge } from '../../components/Badge.js';
import { ErrorState } from '../../components/ErrorState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Skeleton } from '../../components/Skeleton.js';
import { apiRequest } from '../../lib/apiClient.js';
import { usePlans } from '../inventory/useInventory.js';
import { UsagePanel } from '../usage/UsagePanel.js';

import { ActionBar } from './ActionBar.js';
import { simStatusLabel, simStatusTone } from './statusTone.js';

/**
 * A framed panel with a titled head.
 *
 * The three panels here used a lighter border and a radius token
 * (`--brand-radius`) that no other framed object in the app reads, so a screen
 * built out of them did not look like the same product as the estate table it
 * was reached from. `.card` is that one plane, and the ruled head is the shape
 * the dashboard's activity feed already uses — one panel object, four
 * instances.
 */
function Panel({
  id,
  title,
  children,
  wide = false,
}: {
  id: string;
  title: string;
  children: ReactNode;
  /** Span both columns. For a panel holding a table rather than a pair list. */
  wide?: boolean;
}): ReactElement {
  return (
    <section aria-labelledby={id} className={`card overflow-hidden${wide ? ' lg:col-span-2' : ''}`}>
      <div className="border-b border-line px-[var(--space-5)] py-[var(--space-3)]">
        <h2
          id={id}
          className="text-[length:var(--text-lg)] font-semibold tracking-[var(--tracking-tight)] text-body"
        >
          {title}
        </h2>
      </div>
      <div className="p-[var(--space-5)]">{children}</div>
    </section>
  );
}

/**
 * Label/value pairs, monospaced, never truncated.
 *
 * The label is the uppercase micro-label the rest of the product uses for
 * "what this figure is", which puts the *value* first in the reading order by
 * weight — on a screen whose entire job is to be read off and typed into
 * something else. `--tracking-ident` is the estate table's identifier spacing:
 * the same nineteen digits are compared here and there, and they must not be
 * set two different ways.
 */
function IdentityRow({ label, value }: { label: string; value: string | null }): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-[var(--space-1)]">
      <dt className="text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted">
        {label}
      </dt>
      {/* `select-all`: one click takes the whole identifier, which is what a
          person copying it into a ticket actually wants. `break-all` because
          truncating it would defeat the only reason the page exists. */}
      <dd className="select-all break-all font-[family-name:var(--font-mono)] text-[length:var(--text-sm)] tabular-nums tracking-[var(--tracking-ident)] text-body">
        {value ?? '—'}
      </dd>
    </div>
  );
}

/**
 * A label/value pair for prose — an operator, a circle, a date.
 *
 * `IdentityRow` sets its value mono, tabular and `select-all` because those
 * values are identifiers: compared digit by digit and typed into something
 * else. A circle name is not one. Setting "DELHI" and "Private APN with
 * whitelisting" in the same face as an ICCID is what made this screen read as
 * a dump of machine fields rather than a description of a SIM.
 */
function FactRow({ label, value }: { label: string; value: string | null }): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-[var(--space-1)]">
      <dt className="text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted">
        {label}
      </dt>
      <dd className="text-[length:var(--text-sm)] text-body">{value ?? '—'}</dd>
    </div>
  );
}

/** The page's one date format, matching the job detail page's timeline. */
function when(iso: string | null): string | null {
  if (iso === null) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at.toLocaleString();
}

/** The rows of a two-column panel body. */
const FACTS = 'grid gap-x-[var(--space-5)] gap-y-[var(--space-4)] sm:grid-cols-2';

export function SimDetailPage(): ReactElement {
  const { simRef } = useParams<{ simRef: string }>();
  const { tenant, hasPermission, hasFeature } = useAuth();
  // Same as the estate: this tenant is refused the plan catalogue by design.
  // Same as the estate, and the same reason for `tenant != null`: an
  // unresolved tenant must not fire the request this tenant will be refused.
  const plans = usePlans({ enabled: tenant != null && tenant.hasDefaultPlan !== true });

  /**
   * The API guards `GET /sims/usage` with `sims.usage.read` AND the
   * `sims.usage` feature (`sims.routes.ts`, route 24). Rendering the panel
   * unconditionally meant every viewer without either got a 403 error card in
   * the middle of a page they are entitled to — and the request was still
   * fired. Absent, not disabled: to an account that has not bought usage, a
   * greyed panel is a permanent advertisement (same rule as the nav).
   */
  const canSeeUsage = hasPermission(PERMISSION.usageRead) && hasFeature(FEATURE.usage);

  const sim = useQuery<SimSummary>({
    queryKey: ['sim', simRef],
    queryFn: () => apiRequest<SimSummary>(`/sims/${simRef ?? ''}`),
    enabled: Boolean(simRef),
  });

  if (sim.isPending) {
    // The shape of the page it replaces — a header band, then two panels side
    // by side — rather than one grey slab that resolves into something else.
    return (
      <>
        <Skeleton className="mb-[var(--space-6)] h-12 w-64" />
        <div className="grid gap-[var(--space-6)] lg:grid-cols-2">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
      </>
    );
  }
  if (sim.isError || !sim.data) {
    return <ErrorState error={sim.error} onRetry={() => void sim.refetch()} />;
  }

  const data = sim.data;

  return (
    <>
      {/*
        The status moves from the header's description line into a pill.

        It is the same string — `simStatusLabel` returns the network's own
        spelling, tidied for case and nothing else — but as a pill it is the
        same object an operator just read in the estate table's Status column,
        with the same tone and the same glyph. A status set as grey prose under
        the number was the one place in the product where a SIM's state was
        rendered by something other than the pill.
      */}
      <BackLink fallback="/sims" fallbackLabel="SIM estate" />
      <PageHeader
        title={data.msisdn ?? data.simRef}
        eyebrow="SIM estate"
        actions={<Badge tone={simStatusTone(data.status)}>{simStatusLabel(data.status)}</Badge>}
      />

      <div className="grid items-start gap-[var(--space-6)] lg:grid-cols-2">
        <Panel id="identity-heading" title="Identity">
          {/*
            One column below `sm`. Two 19-character identifiers side by side in
            half a phone's width wrap into four lines each and stop being
            comparable, which is the only thing this panel is for.
          */}
          <dl className="grid gap-x-[var(--space-5)] gap-y-[var(--space-4)] sm:grid-cols-2">
            <IdentityRow label="Mobile number" value={data.msisdn} />
            <IdentityRow label="SIM number (ICCID)" value={data.iccid} />
            <IdentityRow label="IMSI" value={data.imsi} />
            <IdentityRow label="eSIM (EID)" value={data.eid} />
          </dl>
        </Panel>

        {/*
          Actions sits second, not last.

          It is what a person came to this page to do, and on a screen that now
          carries six panels it would otherwise be below the fold on every
          laptop. Beside the identity it is confirming, it is the first thing
          read after "is this the right SIM".
        */}
        <Panel id="actions-heading" title="Actions">
          <ActionBar
            sim={data}
            plans={(plans.data ?? []) as PlanSummary[]}
            onSubmitted={() => void sim.refetch()}
          />
        </Panel>

        {/*
          Where this SIM is in its life. For a SIM still at INITIAL the empty
          rows are the answer — "onboarded, never activated" is exactly what an
          operator is checking before they activate it.
        */}
        <Panel id="lifecycle-heading" title="Lifecycle">
          <dl className={FACTS}>
            <FactRow label="Onboarded" value={when(data.onboardedAt)} />
            <FactRow label="Activated" value={when(data.activatedAt)} />
            <FactRow label="Safe custody" value={when(data.safeCustodyAt)} />
            <FactRow label="Expires" value={when(data.expiresAt)} />
          </dl>
        </Panel>

        {/* No `Panel` head: `UsagePanel` renders its own `usage-heading`, and a
            second heading over it would give the section two names. */}
        {canSeeUsage ? (
          <section className="card p-[var(--space-5)]">
            <UsagePanel msisdn={data.msisdn} />
          </section>
        ) : null}
      </div>
    </>
  );
}
